import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Square, MapPin, Type, X, Loader2 } from "lucide-react";

import { useI18n } from "@/context/I18nContext";

type Tool = "box" | "pin" | "text";

/** Append-only evidence annotator. Renders the original image untouched and overlays vector
 *  annotations fetched from /annotations/{attId}; new overlays are POSTed back. Coordinates are
 *  normalized 0..1 so they render correctly at any size. */
export const PhotoAnnotator: React.FC<{ attachmentId: string; onClose: () => void }> = ({ attachmentId, onClose }) => {
  const {
    t: tI18n
  } = useI18n();

  const [imgUrl, setImgUrl] = useState<string | null>(null);
  const [imgErr, setImgErr] = useState(false);
  const [ann, setAnn] = useState<any[]>([]);
  const [tool, setTool] = useState<Tool>("pin");
  const [drag, setDrag] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);
  const [me, setMe] = useState<string | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  const loadAnn = useCallback(() => {
    api.get(`/annotations/${attachmentId}`).then((r) => setAnn(r.data.annotations || [])).catch(() => setAnn([]));
  }, [attachmentId]);

  useEffect(() => {
    let url: string | null = null;
    api.get(`/forms/evidence/${attachmentId}/download`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setImgUrl(url); }).catch(() => setImgErr(true));
    api.get("/auth/me").then((r) => setMe(r.data?.user?.id || r.data?.id || null)).catch(() => {});
    loadAnn();
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [attachmentId, loadAnn]);

  const rel = (e: React.PointerEvent) => {
    const rect = boxRef.current!.getBoundingClientRect();
    return { x: (e.clientX - rect.left) / rect.width, y: (e.clientY - rect.top) / rect.height };
  };

  const post = async (shape: Tool, geometry: any, text?: string) => {
    setBusy(true);
    try { await api.post(`/annotations/${attachmentId}`, { shape, geometry, text, color: "#ef4444" }); loadAnn(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Could not save annotation"); }
    finally { setBusy(false); }
  };

  const onDown = (e: React.PointerEvent) => {
    const p = rel(e);
    if (tool === "pin") { post("pin", p); return; }
    if (tool === "text") { const t = window.prompt("Annotation text"); if (t) post("text", p, t); return; }
    setDrag({ x: p.x, y: p.y, w: 0, h: 0 });
  };
  const onMove = (e: React.PointerEvent) => { if (drag) { const p = rel(e); setDrag((d: any) => ({ ...d, w: p.x - d.x, h: p.y - d.y })); } };
  const onUp = () => {
    if (drag) {
      const g = { x: Math.min(drag.x, drag.x + drag.w), y: Math.min(drag.y, drag.y + drag.h), w: Math.abs(drag.w), h: Math.abs(drag.h) };
      if (g.w > 0.02 && g.h > 0.02) post("box", g);
      setDrag(null);
    }
  };
  const remove = async (id: string) => { try { await api.delete(`/annotations/item/${id}`); loadAnn(); } catch (e: any) { toast.error(e?.response?.data?.detail || "Cannot remove"); } };

  return (
    (<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-3 sm:p-4" data-testid="annotator-overlay" onClick={onClose}>
      <div className="w-full max-w-[calc(100vw-1.5rem)] sm:max-w-2xl rounded-lg bg-background p-4" onClick={(e) => e.stopPropagation()} data-testid="annotator">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex gap-1.5">
            {([["pin", MapPin], ["box", Square], ["text", Type]] as [Tool, any][]).map(([t, Icon]) => (
              <button key={t} onClick={() => setTool(t)} data-testid={`annot-tool-${t}`}
                className={`flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs ${tool === t ? "border-brand bg-brand/10 text-brand" : "border-border"}`}>
                <Icon size={13} /> {t}
              </button>
            ))}
            {busy && <Loader2 size={16} className="animate-spin text-muted-foreground" />}
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="annot-close"><X size={16} /></Button>
        </div>
        <div ref={boxRef} className="relative select-none overflow-hidden rounded-md border border-border bg-muted"
             style={{ aspectRatio: "4 / 3", cursor: "crosshair" }} data-testid="annot-canvas"
             onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp}>
          {imgUrl ? <img src={imgUrl} alt={tI18n("evidence")} className="pointer-events-none h-full w-full object-contain" /> : <div className="flex h-full items-center justify-center text-sm text-muted-foreground" data-testid="annot-img-state">{imgErr ? tI18n("Evidence unavailable") : tI18n("Loading evidence…")}</div>}
          {ann.map((a) => {
            const g = a.geometry || {};
            if (a.shape === "box") return <div key={a.id} className="absolute border-2" style={{ left: `${g.x * 100}%`, top: `${g.y * 100}%`, width: `${g.w * 100}%`, height: `${g.h * 100}%`, borderColor: a.color }} data-testid={`annot-${a.id}`} />;
            if (a.shape === "pin") return <div key={a.id} className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `${g.x * 100}%`, top: `${g.y * 100}%` }} data-testid={`annot-${a.id}`}><MapPin size={20} style={{ color: a.color }} fill={a.color} /></div>;
            return <div key={a.id} className="absolute max-w-[40%] -translate-y-1/2 rounded bg-black/70 px-1.5 py-0.5 text-[11px] text-white" style={{ left: `${g.x * 100}%`, top: `${g.y * 100}%` }} data-testid={`annot-${a.id}`}>{a.text}</div>;
          })}
          {drag && <div className="absolute border-2 border-brand" style={{ left: `${Math.min(drag.x, drag.x + drag.w) * 100}%`, top: `${Math.min(drag.y, drag.y + drag.h) * 100}%`, width: `${Math.abs(drag.w) * 100}%`, height: `${Math.abs(drag.h) * 100}%` }} />}
        </div>
        <div className="mt-3 max-h-28 overflow-auto text-xs" data-testid="annot-list">
          {ann.length === 0 ? <div className="text-muted-foreground">{tI18n("No annotations yet — tap the image to add one.")}</div> :
            ann.map((a) => (
              <div key={a.id} className="flex items-center justify-between border-b border-border py-1">
                <span>{a.shape}{a.text ? `: ${a.text}` : ""} · {a.author}</span>
                {(me === a.author_id) && <button onClick={() => remove(a.id)} data-testid={`annot-del-${a.id}`} className="text-muted-foreground hover:text-red-500"><X size={13} /></button>}
              </div>
            ))}
        </div>
      </div>
    </div>)
  );
};
